import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { 
  Ruler, 
  Plus, 
  Pencil, 
  Trash2, 
  X, 
  Check,
  AlertTriangle,
  Bike
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('veloflux_token')}` };
}

// Size categories for visual grouping
const SIZE_CATEGORIES = {
  letter: ['XS', 'S', 'M', 'L', 'XL', 'XXL'],
};

function getTallaCategory(talla) {
  const upper = talla.toUpperCase();
  if (SIZE_CATEGORIES.letter.includes(upper)) return 'Letras';
  if (/^\d+$/.test(talla)) return 'Numéricas';
  return 'Otras';
}

export default function Tallas() {
  const { isAdmin } = useAuth();
  const [tallas, setTallas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newTalla, setNewTalla] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [bikeCounts, setBikeCounts] = useState({});

  const fetchTallas = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/catalog/tallas`, { headers: getHeaders() });
      setTallas(res.data);
      
      // Fetch bike counts for each talla
      const counts = {};
      for (const t of res.data) {
        try {
          const countRes = await axios.get(`${API}/bicycles?size=${encodeURIComponent(t.talla)}&count_only=true`, { headers: getHeaders() });
          counts[t.id] = countRes.data.count || 0;
        } catch {
          counts[t.id] = 0;
        }
      }
      setBikeCounts(counts);
    } catch (err) {
      toast.error('Error al cargar tallas');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchTallas();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newTalla.trim()) return;
    
    try {
      await axios.post(`${API}/catalog/tallas`, {
        talla: newTalla.trim()
      }, { headers: getHeaders() });
      toast.success('Talla creada correctamente');
      setShowAddDialog(false);
      setNewTalla('');
      fetchTallas();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear talla');
    }
  };

  const handleUpdate = async (id) => {
    try {
      await axios.put(`${API}/catalog/tallas/${id}`, {
        talla: editValue.trim()
      }, { headers: getHeaders() });
      toast.success('Talla actualizada');
      setEditingId(null);
      fetchTallas();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/catalog/tallas/${id}`, { headers: getHeaders() });
      toast.success('Talla eliminada');
      setDeleteConfirm(null);
      fetchTallas();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar. Puede que haya bicicletas usando esta talla.');
    }
  };

  const startEdit = (t) => {
    setEditingId(t.id);
    setEditValue(t.talla);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  // Group tallas by category
  const tallasByCategory = tallas.reduce((acc, t) => {
    const cat = getTallaCategory(t.talla);
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(t);
    return acc;
  }, {});

  // Order categories: Letras, Numéricas, Otras
  const categoryOrder = ['Letras', 'Numéricas', 'Otras'];
  const sortedCategories = categoryOrder.filter(cat => tallasByCategory[cat]?.length > 0);

  return (
    <div className="space-y-6" data-testid="tallas-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-['Barlow_Condensed']">
            Tallas
          </h1>
          <p className="text-slate-500 mt-1">
            Gestiona las tallas de bicicletas disponibles
          </p>
        </div>
        {isAdmin && (
          <Button 
            onClick={() => setShowAddDialog(true)}
            className="bg-orange-500 hover:bg-orange-600"
            data-testid="add-talla-btn"
          >
            <Plus size={18} className="mr-2" />
            Nueva Talla
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-100 rounded-lg">
                <Ruler className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Tallas</p>
                <p className="text-2xl font-bold text-slate-900">{tallas.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <span className="text-blue-600 font-bold text-lg">S/M/L</span>
              </div>
              <div>
                <p className="text-sm text-slate-500">Tallas por Letra</p>
                <p className="text-2xl font-bold text-slate-900">
                  {tallasByCategory['Letras']?.length || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <span className="text-green-600 font-bold text-lg">54</span>
              </div>
              <div>
                <p className="text-sm text-slate-500">Tallas Numéricas</p>
                <p className="text-2xl font-bold text-slate-900">
                  {tallasByCategory['Numéricas']?.length || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tallas Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-['Barlow_Condensed']">Lista de Tallas</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-400">Cargando...</div>
          ) : tallas.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Ruler className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No hay tallas registradas</p>
            </div>
          ) : (
            <div className="space-y-6">
              {sortedCategories.map((category) => (
                <div key={category}>
                  <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                    {category === 'Letras' && <span className="text-blue-500">ABC</span>}
                    {category === 'Numéricas' && <span className="text-green-500">123</span>}
                    {category === 'Otras' && <span className="text-slate-400">...</span>}
                    {category}
                    <Badge variant="outline" className="ml-1">{tallasByCategory[category].length}</Badge>
                  </h3>
                  <div className="overflow-x-auto border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-32">Talla</TableHead>
                          <TableHead className="w-32 text-center">Bicicletas</TableHead>
                          {isAdmin && <TableHead className="w-32 text-right">Acciones</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {tallasByCategory[category].map((t) => (
                          <TableRow key={t.id} data-testid={`talla-row-${t.id}`}>
                            {editingId === t.id ? (
                              <>
                                <TableCell>
                                  <Input
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    className="w-24"
                                    autoFocus
                                  />
                                </TableCell>
                                <TableCell className="text-center">
                                  <Badge variant="outline">{bikeCounts[t.id] || 0}</Badge>
                                </TableCell>
                                <TableCell className="text-right">
                                  <div className="flex justify-end gap-1">
                                    <Button size="sm" variant="ghost" onClick={() => handleUpdate(t.id)}>
                                      <Check size={16} className="text-green-600" />
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={cancelEdit}>
                                      <X size={16} className="text-slate-500" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </>
                            ) : (
                              <>
                                <TableCell>
                                  <Badge 
                                    variant="outline" 
                                    className={`font-mono text-base px-3 py-1 ${
                                      category === 'Letras' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                      category === 'Numéricas' ? 'bg-green-50 text-green-700 border-green-200' :
                                      'bg-slate-50 text-slate-700'
                                    }`}
                                  >
                                    {t.talla}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-center">
                                  <Badge variant="outline">{bikeCounts[t.id] || 0}</Badge>
                                </TableCell>
                                {isAdmin && (
                                  <TableCell className="text-right">
                                    <div className="flex justify-end gap-1">
                                      <Button 
                                        size="sm" 
                                        variant="ghost" 
                                        onClick={() => startEdit(t)}
                                        data-testid={`edit-talla-${t.id}`}
                                      >
                                        <Pencil size={16} className="text-slate-500" />
                                      </Button>
                                      <Button 
                                        size="sm" 
                                        variant="ghost" 
                                        onClick={() => setDeleteConfirm(t)}
                                        className="text-red-500 hover:text-red-700"
                                        data-testid={`delete-talla-${t.id}`}
                                      >
                                        <Trash2 size={16} />
                                      </Button>
                                    </div>
                                  </TableCell>
                                )}
                              </>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <AlertTriangle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Orden de Tallas</p>
              <p>
                Las tallas se ordenan automáticamente: primero las de letra (XS, S, M, L, XL, XXL), 
                luego las numéricas en orden ascendente (47, 49, 51...), y finalmente cualquier 
                otro formato en orden alfabético.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add Talla Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent data-testid="add-talla-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ruler className="w-5 h-5 text-orange-500" />
              Nueva Talla
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Talla</label>
              <Input
                value={newTalla}
                onChange={(e) => setNewTalla(e.target.value)}
                placeholder="Ej: 54, M, XL"
                required
                data-testid="new-talla-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Introduce la talla (número o letra)
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-orange-500 hover:bg-orange-600">
                Crear Talla
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Eliminar Talla</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            ¿Estás seguro de que quieres eliminar la talla <strong>{deleteConfirm?.talla}</strong>?
          </p>
          {(bikeCounts[deleteConfirm?.id] || 0) > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
              <strong>Advertencia:</strong> Esta talla tiene {bikeCounts[deleteConfirm?.id]} bicicletas asociadas.
              No podrás eliminarla mientras existan bicicletas con esta talla.
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm?.id)}>
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
